<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Receipt Payment Statement
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Date Filters -->
                <v-row class="py-2 pl-2 align-center">
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy" 
                      v-model="filters.from_date" 
                      label="From Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy" 
                      v-model="filters.to_date" 
                      label="To Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                      <v-autocomplete
                      v-model="filters.cost_center_id"
                      :items="costCenterOptions"
                      item-title="text"
                      item-value="value"
                      label="Cost Center"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-inner-icon="mdi-office-building"
                      clearable
                      :loading="loadingCostCenters"
                    ></v-autocomplete>
                  </v-col>
                   <v-col lg="3" md="12" cols="12">
                    <div class="d-flex justify-start align-center flex-wrap">
                      <v-btn class="bg-primary mb-4" type="submit" @click="loadReceiptPaymentStatement" elevation="2" >
                        <v-icon left>mdi-magnify</v-icon> Search
                      </v-btn>
                    </div>
                  </v-col>
                
                </v-row>

                <!-- Export Buttons -->
                <v-row v-if="statementData && statementData.vouchers && statementData.vouchers.length > 0" class="py-2 mt-2">
                  <v-col cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToExcel" :disabled="!statementData">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF" :disabled="!statementData">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Receipt Payment Statement Report -->
                <v-card v-if="statementData && !loading && statementData.vouchers && statementData.vouchers.length > 0" class="mt-2">
                  <v-card-title class="text-center">
                    <div class="w-100">
                      <div class="text-h5 font-weight-bold">Receipt Payment Statement for 
                    </div>
                      <div class="text-h5 font-weight-bold">
                         {{ formattedDate(filters.from_date) }} to {{ formattedDate(filters.to_date) }}</div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <!-- Statement Report -->
                    <div class="statement-report">
                      <!-- Handle voucher data structure -->
                      <div class="statement-account-section">
                        <!-- General Header -->
                        <div class="account-header">
                          <strong>Receipt Payment Statement</strong>
                        </div>

                        <!-- Statement Table -->
                        <div class="statement-table-exact">
                          <!-- Table Header -->
                          <div class="statement-header">
                            <div class="header-serial">S/L</div>
                            <div class="header-date">Date</div>
                            <div class="header-particular">Particulars</div>
                            <div class="header-payment-mode">Payment Mode</div>
                            <div class="header-receipt">Receipt Amount</div>
                            <div class="header-payment">Payment Amount</div>
                            <div class="header-profit">Balance</div>
                          </div>

                          <!-- Voucher Rows -->
                          <div v-for="(voucher, index) in statementData.vouchers" :key="voucher.id" class="statement-row">
                            <div class="statement-serial">{{ index + 1 }}</div>
                            <div class="statement-date">{{ formattedDate(voucher.voucher_date) }}</div>
                            <div class="statement-particular">{{ voucher.remarks || (voucher.receipt_amount ? 'Receipt Transaction' : 'Payment Transaction') }}</div>
                            <div class="statement-payment-mode">{{ voucher.payment_account_name || 'Cash' }}</div>
                            <div class="statement-receipt">{{ voucher.receipt_amount ? formatCurrency(voucher.receipt_amount) : '-' }}</div>
                            <div class="statement-payment">{{ voucher.payment_amount ? formatCurrency(voucher.payment_amount) : '-' }}</div>
                            <div class="statement-profit">{{ calculateProfit(voucher) }}</div>
                          </div>

                          <!-- Amount in Words Row -->
                          <div class="statement-row amount-in-words">
                            <div class="statement-serial"></div>
                            <div class="statement-date"></div>
                            <div class="statement-particular"></div>
                            <div class="statement-payment-mode">Amount in Words: {{ getAmountInWords(getNetAmount()) }}</div>
                            <div class="statement-receipt"></div>
                            <div class="statement-payment"></div>
                            <div class="statement-profit"></div>
                          </div>

                          <!-- Net Profit Row -->
                          <div class="statement-row net-profit">
                            <div class="statement-serial"></div>
                            <div class="statement-date"></div>
                            <div class="statement-particular"></div>
                            <div class="statement-payment-mode"></div>
                            <div class="statement-receipt"></div>
                            <div class="statement-payment">Net Profit</div>
                            <div class="statement-profit">{{ formatCurrency(getNetAmount()) }}</div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- No Data Message -->
                <v-card v-else-if="!loading && (!statementData || !statementData.vouchers || statementData.vouchers.length === 0)" class="mt-2">
                  <v-card-text class="text-center py-8">
                    <v-icon size="64" color="grey">mdi-currency-usd</v-icon>
                    <div class="text-h6 mt-2">No Receipt Payment Statement Data Found</div>
                    <div class="text-body-2 text-grey">Please adjust your filters and try again</div>
                  </v-card-text>
                </v-card>

                <!-- Loading State -->
                <v-card v-if="loading" class="mt-2">
                  <v-card-text class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <div class="text-h6 mt-2">Loading Receipt Payment Statement...</div>
                  </v-card-text>
                </v-card>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import formattedDate from '@/plugins/dateFormatter';
import { useStore } from 'vuex'

const store = useStore()
import { mapGetters } from 'vuex'

export default {
  name: 'ReceiptPaymentStatementReport',
  data() {
    return {
      panel: [0],
      loading: false,
      loadingCostCenters: false,
      statementData: null,
      costCenterOptions: [],
      filters: {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        cost_center_id: null
      }
    }
  },
  computed: {
    ...mapGetters(['getBranchId'])
  },
  mounted() {
    this.loadCostCenters()
  },
  methods: {
    formattedDate,
    async loadCostCenters() {
      this.loadingCostCenters = true
      try {
        const response = await this.$store.dispatch('getCostCenters', {})
        this.costCenterOptions = response.data.map(item => ({
          text: `${item.code} - ${item.name}`,
          value: item.id
        }))
      } catch (error) {
        console.error('Error loading cost centers:', error)
        this.$toast.error('Failed to load cost centers')
      } finally {
        this.loadingCostCenters = false
      }
    },

    async loadReceiptPaymentStatement() {
      this.loading = true
      try {
        const params = {
          from_date: formattedDate(this.filters.from_date),
          to_date: formattedDate(this.filters.to_date),
          acc_cost_center_id: this.filters.cost_center_id
        }
        
        // Using the proper receipt-and-payment API endpoint
        const response = await this.$store.dispatch('getReceiptAndPayment', params)
        
        if (response.success) {
          this.statementData = response.data
          this.$toast.success('Receipt Payment Statement generated successfully')
        } else {
          this.$toast.error(response.message || 'Failed to generate receipt payment statement')
        }
      } catch (error) {
        console.error('Error loading receipt payment statement:', error)
        this.$toast.error('Failed to load receipt payment statement')
        this.statementData = null
      } finally {
        this.loading = false
      }
    },

    resetFilters() {
      this.filters = {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        cost_center_id: null
      }
      this.statementData = null
    },

    formatCurrency(amount) {
      if (!amount || amount === 0) return '0.00'
      return parseFloat(amount).toFixed(2)
    },

    formatBalance(balance) {
      if (!balance) return '0.00'
      
      // Handle wrapped balance object with nature (future compatibility)
      if (typeof balance === 'object' && balance.balance !== undefined) {
        const amt = parseFloat(balance.balance).toFixed(2)
        const nature = balance.nature || ''
        return nature ? `${amt} ${nature}` : amt
      }
      
      // Handle string format from API - e.g., "15000.00" or "(2000.00)" for negative
      if (typeof balance === 'string') {
        // If already formatted with parentheses for negative, return as-is
        if (balance.includes('(') && balance.includes(')')) {
          return balance
        }
        // Otherwise parse and format
        const num = parseFloat(balance.replace(/[(),]/g, ''))
        return isNaN(num) ? '0.00' : num.toFixed(2)
      }
      
      // Handle simple number
      return parseFloat(balance).toFixed(2)
    },

    getPartyName(transaction) {
      // Try to get party name from break down accounts
      if (transaction.break_down && transaction.break_down.length > 0) {
        const partyAccount = transaction.break_down.find(bd => 
          bd.account_head && 
          (bd.account_head.name.toLowerCase().includes('supplier') || 
           bd.account_head.name.toLowerCase().includesный('customer') ||
           bd.account_head.name.toLowerCase().includes('party'))
        )
        return partyAccount ? partyAccount.account_head.name : 'Local supplier'
      }
      return 'Local supplier'
    },

    getChequeNumber(transaction) {
      // This would need to be provided by the backend API
      // For now, return empty string
      return ''
    },

    getAccountTransactions(account) {
      const transactions = []
      
      // The receipt-and-payment API returns a different structure
      // Handle both receipt and payment transactions from the API response
      if (account.transactions && account.transactions.length > 0) {
        account.transactions.forEach(transaction => {
          transactions.push({
            ...transaction,
            type: transaction.type || (transaction.receipt_amount ? 'receipt' : 'payment')
          })
        })
      }
      
      // Fallback: Add receipts and payments if they exist separately
      if (account.receipts && account.receipts.length > 0) {
        account.receipts.forEach(receipt => {
          transactions.push({
            ...receipt,
            type: 'receipt'
          })
        })
      }
      
      if (account.payments && account.payments.length > 0) {
        account.payments.forEach(payment => {
          transactions.push({
            ...payment,
            type: 'payment'
          })
        })
      }
      
      // Sort by date
      return transactions.sort((a, b) => new Date(a.voucher_date) - new Date(b.voucher_date))
    },

    calculateProfit(transaction) {
      if (transaction.receipt_amount && transaction.payment_amount) {
        return this.formatCurrency(transaction.receipt_amount - transaction.payment_amount)
      } else if (transaction.receipt_amount) {
        return this.formatCurrency(transaction.receipt_amount)
      } else if (transaction.payment_amount) {
        return '-' + this.formatCurrency(transaction.payment_amount)
      }
      return '0.00'
    },

    getTotalReceipt(account) {
      let total = 0
      
      // Handle flat transaction structure (when account is actually the main data object)
      if (account.transactions && account.transactions.length > 0) {
        total += account.transactions.reduce((sum, transaction) => {
          return sum + (transaction.receipt_amount || 0)
        }, 0)
      }
      
      // Handle grouped account structure
      if (account.receipts && account.receipts.length > 0) {
        total += account.receipts.reduce((sum, receipt) => sum + (receipt.receipt_amount || 0), 0)
      }
      
      // Use API provided total if available
      if (account.total_receipt !== undefined) {
        return account.total_receipt
      }
      
      return total
    },

    getTotalPayment(account) {
      let total = 0
      
      // Handle flat transaction structure (when account is actually the main data object)
      if (account.transactions && account.transactions.length > 0) {
        total += account.transactions.reduce((sum, transaction) => {
          return sum + (transaction.payment_amount || 0)
        }, 0)
      }
      
      // Handle grouped account structure
      if (account.payments && account.payments.length > 0) {
        total += account.payments.reduce((sum, payment) => sum + (payment.payment_amount || 0), 0)
      }
      
      // Use API provided total if available
      if (account.total_payment !== undefined) {
        return account.total_payment
      }
      
      return total
    },

    getAmountInWords(amount) {
      // Convert to number and handle decimal places
      const numAmount = parseFloat(amount) || 0
      
      if (numAmount === 0) return 'Zero Taka Only'
      
      // Handle negative amounts
      const isNegative = numAmount < 0
      const absAmount = Math.abs(numAmount)
      
      // Simple number to words conversion for amounts
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
      const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
      
      // Function to convert numbers to words
      function convertToWords(num) {
        if (num === 0) return ''
        if (num < 10) return ones[num]
        if (num < 20) return teens[num - 10]
        if (num < 100) {
          const tenDigit = Math.floor(num / 10)
          const oneDigit = num % 10
          return tens[tenDigit] + (oneDigit > 0 ? ' ' + ones[oneDigit] : '')
        }
        if (num < 1000) {
          const hundredDigit = Math.floor(num / 100)
          const remainder = num % 100
          return ones[hundredDigit] + ' Hundred' + (remainder > 0 ? ' ' + convertToWords(remainder) : '')
        }
        if (num < 100000) {
          const thousandDigit = Math.floor(num / 1000)
          const remainder = num % 1000
          return convertToWords(thousandDigit) + ' Thousand' + (remainder > 0 ? ' ' + convertToWords(remainder) : '')
        }
        if (num < 10000000) {
          const lakhDigit = Math.floor(num / 100000)
          const remainder = num % 100000
          return convertToWords(lakhDigit) + ' Lakh' + (remainder > 0 ? ' ' + convertToWords(remainder) : '')
        }
        if (num < 1000000000) {
          const croreDigit = Math.floor(num / 10000000)
          const remainder = num % 10000000
          return convertToWords(croreDigit) + ' Crore' + (remainder > 0 ? ' ' + convertToWords(remainder) : '')
        }
        return num.toString() + ' Taka Only'
      }
      
      const integerPart = Math.floor(absAmount)
      const decimalPart = Math.round((absAmount - integerPart) * 100)
      
      let result = convertToWords(integerPart)
      if (result) result += ' Taka'
      
      if (decimalPart > 0) {
        if (result) result += ' and '
        result += convertToWords(decimalPart) + ' Paisa'
      }
      
      if (!result) result = 'Zero'
      result += ' Only'
      
      return (isNegative ? 'Minus ' : '') + result
    },

    getNetAmount() {
      if (!this.statementData || !this.statementData.totals) {
        return 0
      }
      
      const totalReceipt = parseFloat(this.statementData.totals.total_receipt) || 0
      const totalPayment = parseFloat(this.statementData.totals.total_payment) || 0
      
      return totalReceipt - totalPayment
    },

    exportToExcel() {
      if (!this.statementData || !this.statementData.vouchers || this.statementData.vouchers.length === 0) return

      const data = [
        ['Receipt Payment Statement'],
        [`Date: ${this.formattedDate(this.filters.from_date)} to ${this.formattedDate(this.filters.to_date)}`],
        []
      ]

      // Table Header
      data.push(['S/L', 'Date', 'Particulars', 'Payment Mode', 'Receipt Amount', 'Payment Amount', 'Balance'])
      
      // Voucher Rows
      this.statementData.vouchers.forEach((voucher, index) => {
        const particular = voucher.remarks || (voucher.receipt_amount ? 'Receipt Transaction' : 'Payment Transaction')
        
        data.push([
          index + 1,
          this.formattedDate(voucher.voucher_date),
          particular,
          voucher.payment_account_name || 'Cash',
          voucher.receipt_amount ? this.formatCurrency(voucher.receipt_amount) : '-',
          voucher.payment_amount ? this.formatCurrency(voucher.payment_amount) : '-',
          this.calculateProfit(voucher)
        ])
      })

      // Amount in Words
      data.push([
        '',
        '',
        '',
        'Amount in Words: ' + this.getAmountInWords(this.getNetAmount()),
        '',
        '',
        ''
      ])

      // Net Profit
      data.push([
        '',
        '',
        '',
        '',
        '',
        'Net Profit',
        this.formatCurrency(this.getNetAmount())
      ])

      this.$exportTableToExcel(data, 'receipt_payment_statement_' + this.$fileDate())
    },

    exportToPDF() {
      if (!this.statementData || !this.statementData.vouchers || this.statementData.vouchers.length === 0) return

      const columns = ['S/L', 'Date', 'Particulars', 'Payment Mode', 'Receipt Amount', 'Payment Amount', 'Balance']
      const rows = []

      // Voucher Rows
      this.statementData.vouchers.forEach((voucher, index) => {
        const particular = voucher.remarks || (voucher.receipt_amount ? 'Receipt Transaction' : 'Payment Transaction')
        
        rows.push([
          index + 1,
          this.formattedDate(voucher.voucher_date),
          particular,
          voucher.payment_account_name || 'Cash',
          voucher.receipt_amount ? this.formatCurrency(voucher.receipt_amount) : '-',
          voucher.payment_amount ? this.formatCurrency(voucher.payment_amount) : '-',
          this.calculateProfit(voucher)
        ])
      })

      // Amount in Words
      rows.push([
        '',
        '',
        '',
        'Amount in Words: ' + this.getAmountInWords(this.getNetAmount()),
        '',
        '',
        ''
      ])

      // Net Profit
      rows.push([
        '',
        '',
        '',
        '',
        '',
        'Net Profit',
        this.formatCurrency(this.getNetAmount())
      ])

      this.$exportTableToPDF(
        columns,
        rows,
        `Receipt Payment Statement - ${this.formattedDate(this.filters.from_date)} to ${this.formattedDate(this.filters.to_date)}`,
        'receipt_payment_statement_' + this.$fileDate(),
        'landscape',
        {
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fontSize: 9, fillColor: [66, 139, 202], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' },  // S/L
            1: { cellWidth: 20, halign: 'center' },  // Date
            2: { cellWidth: 50, halign: 'left' },    // Particulars
            3: { cellWidth: 25, halign: 'center' },  // Payment Mode
            4: { cellWidth: 25, halign: 'right' },   // Receipt Amount
            5: { cellWidth: 25, halign: 'right' },   // Payment Amount
            6: { cellWidth: 25, halign: 'right' }    // Balance
          },
          didParseCell: function(data) {
            // Bold style for Net Profit rows
            if (data.cell.text[0] === 'Net Profit') {
              data.cell.styles.fontStyle = 'bold'
              data.cell.styles.fillColor = [240, 240, 240]
            }
          }
        }
      )
    }
  }
}
</script>

<style scoped>
.panel-header {
  font-weight: bold;
  font-size: 1.1rem;
}

/* Statement Table Style */
.statement-account-section {
  margin-bottom: 20px;
}

.account-header {
  padding: 2px 4px;
  border-bottom: 1px solid #000;
  font-weight: bold;
  text-align: center;
  font-size: 12px;
}

.statement-table-exact {
  font-family: 'Roboto', sans-serif;
  width: 100%;
  background-color: white;
  border: 1px solid #000;
  overflow: hidden;
  margin-bottom: 20px;
  table-layout: fixed;
}

/* Header Styles */
.statement-header {
  display: flex;
  align-items: stretch;
  font-weight: bold;
  border-bottom: 1px solid #000;
}

.header-serial {
  width: 60px;
  min-width: 60px;
  max-width: 60px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-date {
  width: 90px;
  min-width: 90px;
  max-width: 90px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-particular {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: left;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
}

.header-payment-mode {
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-receipt {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-payment {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-profit {
  flex: 1;
  padding: 2px 4px;
  font-size: 12px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* Row Styles */
.statement-row {
  display: flex;
  align-items: stretch;
  min-height: 32px;
  border-bottom: 1px solid #000;
}

.statement-serial {
  width: 60px;
  min-width: 60px;
  max-width: 60px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.statement-date {
  width: 90px;
  min-width: 90px;
  max-width: 90px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.statement-particular {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: left;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
}

.statement-payment-mode {
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.statement-receipt {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: right;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.statement-payment {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: right;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.statement-profit {
  flex: 1;
  padding: 2px 4px;
  font-size: 12px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.opening-balance {
  font-style: italic;
}

.closing-balance {
  font-style: italic;
  font-weight: bold;
}

.account-total {
  border-top: 1px solid #000;
  font-weight: bold;
}

.receipt-row {
  background-color: #f8f9fa;
}

.payment-row {
  background-color: #fff3cd;
}

.breakdown-details {
  font-size: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.breakdown-item {
  padding: 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  min-height: 12px;
}

.breakdown-name {
  font-weight: 400;
  flex: 1;
  text-align: left;
}

.breakdown-amount {
  font-weight: 500;
  text-align: right;
}

.breakdown-subtotal {
  padding: 1px 0;
  font-weight: 500;
  text-align: right;
  border-top: 1px solid #000;
  margin-top: 1px;
  min-height: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.breakdown-total {
  padding: 1px 0;
  font-weight: bold;
  text-align: right;
  border-top: 1px solid #000;
  margin-top: 1px;
  min-height: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.account-spacing {
  height: 20px;
}

.amount-in-words {
  font-style: italic;
}

.net-profit {
  font-weight: bold;
  background-color: #f8f9fa;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .statement-date,
  .statement-voucher,
  .statement-particular,
  .statement-receipt,
  .statement-payment,
  .statement-balance {
    font-size: 10px;
    padding: 2px 4px;
  }
  
  .header-date,
  .header-voucher,
  .header-particular,
  .header-receipt,
  .header-payment,
  .header-balance {
    font-size: 10px;
    padding: 2px 4px;
  }
}
</style>
